import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { AssetValue, Market, Order } from "@module-bindings/types";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { useHandlePlaceOrderRequest } from "@/hooks/useHandlePlaceOrderRequest";
import { useNextNonce } from "@/hooks/useNextNonce";
import {
  aggregateAssetBalances,
  assetIdKey,
  assetIdToApi,
  findAssetBalance,
  formatAssetLabel,
  formatAssetValue,
  getAssetDecimals,
  marketAssetToWalletId,
} from "@/lib/marketUtils";
import { toast } from "sonner";
import type { OrderType, TradeSide } from "./useTradingViewState";

const FRAC_BITS = 60n;
const SLIPPAGE_BUFFER_PCT = 2;

function fixedPointToDecimal(raw: bigint): string {
  const integer = raw >> FRAC_BITS;
  const fractional = raw & ((1n << FRAC_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRAC_BITS) / 2n) >> FRAC_BITS;
  const fractionalString = scaled.toString().padStart(18, "0").replace(/0+$/, "").slice(0, 6);
  return fractionalString ? `${integer}.${fractionalString}` : integer.toString();
}

function computeMarketPrice(
  quantity: bigint,
  side: "Bid" | "Ask",
  opposingOrders: readonly Order[],
): string {
  let remaining = quantity;
  let worstPrice = 0n;

  for (const order of opposingOrders) {
    if (remaining <= 0n) break;
    worstPrice = BigInt(order.price);
    remaining -= BigInt(order.remaining) < remaining ? BigInt(order.remaining) : remaining;
  }

  if (worstPrice === 0n) {
    return side === "Bid" ? "999999999" : "0.000000000000000001";
  }

  const buffer = worstPrice * BigInt(SLIPPAGE_BUFFER_PCT) / 100n;
  const limitPrice = side === "Bid"
    ? worstPrice + buffer
    : worstPrice > buffer ? worstPrice - buffer : 1n;

  const integer = limitPrice >> FRAC_BITS;
  const fractional = limitPrice & ((1n << FRAC_BITS) - 1n);
  if (fractional === 0n) return integer.toString();
  const scaled = (fractional * 1_000_000_000_000_000_000n + (1n << FRAC_BITS) / 2n) >> FRAC_BITS;
  const fractionalString = scaled.toString().padStart(18, "0").replace(/0+$/, "");
  return `${integer}.${fractionalString}`;
}

interface UseTradeFormArgs {
  selectedMarket: Market | null;
  tradeSide: TradeSide;
  onTradeSideChange: (side: TradeSide) => void;
  orderType: OrderType;
  limitPrice: string;
  onLimitPriceChange: (price: string) => void;
  onDeposit?: (assetId?: string) => void;
  depositDisabled?: boolean;
}

export function useTradeForm({
  selectedMarket,
  tradeSide,
  onTradeSideChange,
  orderType,
  limitPrice,
  onLimitPriceChange,
  onDeposit,
  depositDisabled = false,
}: UseTradeFormArgs) {
  const [swapAmount, setSwapAmount] = useState("");
  const [sliderValue, setSliderValue] = useState(0);
  const { accounts, deposits, myAccountId } = useGummiwormContext();
  const { mutateAsync: placeOrder, isPending: isPlacingOrder } = useHandlePlaceOrderRequest();
  const [allOrders] = useTable(tables.orders);
  const nextNonce = useNextNonce();

  const myAccount = useMemo(
    () => accounts.find((account) => account.accountId === myAccountId),
    [accounts, myAccountId],
  );

  const isSellingSide = tradeSide === "sell";
  const baseAsset = selectedMarket?.tradingPair.base ?? null;
  const quoteAsset = selectedMarket?.tradingPair.quote ?? null;
  const baseLabel = baseAsset ? formatAssetLabel(baseAsset) : null;
  const quoteLabel = quoteAsset ? formatAssetLabel(quoteAsset) : null;

  const spendToken = useMemo(() => {
    const asset = isSellingSide ? baseAsset : quoteAsset;
    if (!asset) return null;
    return { asset, key: assetIdKey(asset), label: formatAssetLabel(asset) };
  }, [baseAsset, isSellingSide, quoteAsset]);

  const receiveToken = useMemo(() => {
    const asset = isSellingSide ? quoteAsset : baseAsset;
    if (!asset) return null;
    return { asset, key: assetIdKey(asset), label: formatAssetLabel(asset) };
  }, [baseAsset, isSellingSide, quoteAsset]);

  const spendTokenOptions = useMemo(() => {
    const options = [];
    if (baseAsset) {
      options.push({ assetId: assetIdKey(baseAsset), name: formatAssetLabel(baseAsset) });
    }
    if (quoteAsset) {
      options.push({ assetId: assetIdKey(quoteAsset), name: formatAssetLabel(quoteAsset) });
    }
    return options;
  }, [baseAsset, quoteAsset]);

  const fallbackAssets = useMemo<AssetValue[]>(() => {
    if (myAccountId == null) return [];

    const absorbedAssets = deposits
      .filter((deposit) => deposit.accountId === myAccountId && deposit.absorbedInBlock !== 0n && deposit.rejectedInBlock === 0n)
      .flatMap((deposit) => deposit.value.assets);

    return aggregateAssetBalances(absorbedAssets).map((balance) => ({
      asset: balance.asset,
      value: balance.value,
    }));
  }, [deposits, myAccountId]);

  const availableAssets = useMemo(
    () => (myAccount?.uncommittedValue.assets.length ? myAccount.uncommittedValue.assets : fallbackAssets),
    [fallbackAssets, myAccount],
  );

  const spendBalance = useMemo(() => {
    if (!spendToken) return null;
    const raw = findAssetBalance(availableAssets, spendToken.key);
    return { raw, formatted: formatAssetValue(spendToken.asset, raw) };
  }, [availableAssets, spendToken]);

  const opposingOrders = useMemo(() => {
    if (!selectedMarket) return [];
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    const opposingSide = isSellingSide ? "Bid" : "Ask";
    const orders = allOrders.filter((order) => {
      if (BigInt(order.remaining) === 0n) return false;
      return assetIdKey(order.pair.base) === baseKey &&
        assetIdKey(order.pair.quote) === quoteKey &&
        order.side.tag === opposingSide;
    });

    orders.sort((a, b) =>
      opposingSide === "Bid"
        ? (BigInt(b.price) > BigInt(a.price) ? 1 : BigInt(b.price) < BigInt(a.price) ? -1 : 0)
        : (BigInt(a.price) > BigInt(b.price) ? 1 : BigInt(a.price) < BigInt(b.price) ? -1 : 0),
    );

    return orders;
  }, [allOrders, isSellingSide, selectedMarket]);

  const opposingLiquidity = useMemo(() => {
    let total = 0n;
    for (const order of opposingOrders) total += BigInt(order.remaining);
    return total;
  }, [opposingOrders]);

  const hasLiquidity = opposingLiquidity > 0n;

  const marketMaxRaw = useMemo(() => {
    if (opposingLiquidity <= 0n) return null;
    if (isSellingSide) return opposingLiquidity;

    let total = 0n;
    for (const order of opposingOrders) {
      total += (BigInt(order.remaining) * BigInt(order.price)) >> FRAC_BITS;
    }
    return total;
  }, [isSellingSide, opposingLiquidity, opposingOrders]);

  const effectiveMaxRaw = useMemo(() => {
    if (!spendBalance || spendBalance.raw <= 0n) return 0n;
    if (orderType === "market") {
      if (marketMaxRaw === null || marketMaxRaw <= 0n) return 0n;
      return marketMaxRaw < spendBalance.raw ? marketMaxRaw : spendBalance.raw;
    }
    return spendBalance.raw;
  }, [marketMaxRaw, orderType, spendBalance]);

  const setAmountFromPercent = useCallback((pct: number) => {
    setSliderValue(pct);
    if (!spendToken || effectiveMaxRaw <= 0n || pct === 0) {
      setSwapAmount("");
      return;
    }

    const raw = effectiveMaxRaw * BigInt(pct) / 100n;
    setSwapAmount(formatAssetValue(spendToken.asset, raw));
  }, [effectiveMaxRaw, spendToken]);

  const handleSliderChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const pct = Number(event.target.value);
    setSliderValue(pct);

    if (!spendToken || effectiveMaxRaw <= 0n || pct === 0) {
      setSwapAmount("");
      return;
    }

    const raw = effectiveMaxRaw * BigInt(pct) / 100n;
    setSwapAmount(formatAssetValue(spendToken.asset, raw));
  }, [effectiveMaxRaw, spendToken]);

  const handleAmountChange = useCallback((value: string) => {
    if (spendToken && value && effectiveMaxRaw > 0n) {
      const num = parseFloat(value.replace(/,/g, ""));
      if (!Number.isNaN(num) && num > 0) {
        const decimals = getAssetDecimals(spendToken.asset);
        const raw = BigInt(Math.round(num * 10 ** decimals));
        if (raw > effectiveMaxRaw) {
          setSwapAmount(formatAssetValue(spendToken.asset, effectiveMaxRaw));
          return;
        }
      }
    }

    setSwapAmount(value);
  }, [effectiveMaxRaw, spendToken]);

  const handleSelectToken = useCallback((assetId: string) => {
    if (!selectedMarket) return;
    const baseKey = assetIdKey(selectedMarket.tradingPair.base);
    const quoteKey = assetIdKey(selectedMarket.tradingPair.quote);
    if (assetId === baseKey) {
      onTradeSideChange("sell");
      return;
    }
    if (assetId === quoteKey) {
      onTradeSideChange("buy");
    }
  }, [onTradeSideChange, selectedMarket]);

  const numericAmount = parseFloat(swapAmount.replace(/,/g, "")) || 0;
  const numericPrice = parseFloat(limitPrice) || 0;
  const spendDecimals = spendToken ? getAssetDecimals(spendToken.asset) : 6;

  const { receiveAmount, avgFillPrice } = useMemo(() => {
    const empty = { receiveAmount: "", avgFillPrice: "" };
    if (numericAmount <= 0 || !receiveToken) return empty;

    if (orderType === "limit" && numericPrice > 0) {
      const result = isSellingSide
        ? numericAmount * numericPrice
        : numericAmount / numericPrice;
      return { receiveAmount: result > 0 ? result.toFixed(6) : "", avgFillPrice: "" };
    }

    if (orderType !== "market" || opposingOrders.length === 0) return empty;

    const rawSpendAmount = BigInt(Math.round(numericAmount * 10 ** spendDecimals));
    let remaining = rawSpendAmount;
    let receiveRaw = 0n;
    let baseFilled = 0n;
    let quoteFilled = 0n;
    const hitPrices = new Set<bigint>();

    if (isSellingSide) {
      for (const order of opposingOrders) {
        if (remaining <= 0n) break;
        const orderRemaining = BigInt(order.remaining);
        const orderPrice = BigInt(order.price);
        const fill = remaining < orderRemaining ? remaining : orderRemaining;
        const quoteReceived = (fill * orderPrice) >> FRAC_BITS;
        receiveRaw += quoteReceived;
        baseFilled += fill;
        quoteFilled += quoteReceived;
        hitPrices.add(orderPrice);
        remaining -= fill;
      }
    } else {
      for (const order of opposingOrders) {
        if (remaining <= 0n) break;
        const orderRemaining = BigInt(order.remaining);
        const orderPrice = BigInt(order.price);
        const baseCanBuy = (remaining << FRAC_BITS) / orderPrice;
        const fill = baseCanBuy < orderRemaining ? baseCanBuy : orderRemaining;
        const quoteCost = (fill * orderPrice) >> FRAC_BITS;
        receiveRaw += fill;
        baseFilled += fill;
        quoteFilled += quoteCost;
        hitPrices.add(orderPrice);
        remaining -= quoteCost;
      }
    }

    if (receiveRaw <= 0n) return empty;

    let avg = "";
    if (hitPrices.size === 1) {
      avg = fixedPointToDecimal(hitPrices.values().next().value!);
    } else if (baseFilled > 0n && quoteFilled > 0n) {
      const precision = 1_000_000_000_000_000_000n;
      const scaled = (quoteFilled * precision) / baseFilled;
      const whole = scaled / precision;
      const fractional = scaled % precision;
      const fractionalString = fractional.toString().padStart(18, "0").replace(/0+$/, "").slice(0, 6);
      avg = fractionalString ? `${whole}.${fractionalString}` : whole.toString();
    }

    return {
      receiveAmount: formatAssetValue(receiveToken.asset, receiveRaw),
      avgFillPrice: avg,
    };
  }, [isSellingSide, limitPrice, numericAmount, numericPrice, opposingOrders, orderType, receiveToken, spendDecimals]);

  const handlePlaceOrder = useCallback(async () => {
    if (!selectedMarket || !spendToken || !myAccount || !swapAmount) return;

    const side: "Bid" | "Ask" = isSellingSide ? "Ask" : "Bid";
    const decimals = getAssetDecimals(spendToken.asset);
    const cleanAmount = swapAmount.replace(/,/g, "");
    const parts = cleanAmount.split(".");
    const wholePart = BigInt(parts[0] || "0");
    const fractionalString = (parts[1] || "").padEnd(decimals, "0").slice(0, decimals);
    const fractionalPart = fractionalString ? BigInt(fractionalString) : 0n;
    const rawQuantity = wholePart * 10n ** BigInt(decimals) + fractionalPart;

    if (rawQuantity <= 0n) return;

    let price: string;
    if (orderType === "limit") {
      const trimmed = limitPrice.trim();
      if (!trimmed || !/^\d*\.?\d{0,6}$/.test(trimmed) || trimmed === ".") {
        toast.error("Enter a valid price (up to 6 decimal places)");
        return;
      }
      price = trimmed.startsWith(".") ? `0${trimmed}` : trimmed;
    } else {
      price = computeMarketPrice(rawQuantity, side, opposingOrders);
    }

    try {
      await placeOrder({
        tradingPair: {
          base: assetIdToApi(selectedMarket.tradingPair.base),
          quote: assetIdToApi(selectedMarket.tradingPair.quote),
        },
        side,
        price,
        quantity: Number(rawQuantity),
        destination: null,
        nonce: nextNonce()!,
      });

      toast.success("Order placed!");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to place order");
    }
  }, [
    isSellingSide,
    limitPrice,
    myAccount,
    nextNonce,
    opposingOrders,
    orderType,
    placeOrder,
    selectedMarket,
    spendToken,
    swapAmount,
  ]);

  const feePercent = selectedMarket
    ? Number(selectedMarket.takerFee.numerator) / Number(selectedMarket.takerFee.denominator) * 100
    : null;
  const feeRate = feePercent !== null ? feePercent / 100 : null;
  const accentTextClass = isSellingSide ? "text-sr-salmon" : "text-sr-mint";
  const accentSurfaceClass = isSellingSide
    ? "bg-sr-salmon/10 text-sr-salmon hover:bg-sr-salmon/20"
    : "bg-sr-mint/10 text-sr-mint hover:bg-sr-mint/20";
  const sliderFillClass = isSellingSide ? "bg-sr-salmon" : "bg-sr-mint";
  const sliderDotClass = isSellingSide ? "border-sr-salmon bg-sr-salmon" : "border-sr-mint bg-sr-mint";
  const sliderThumbStyle = isSellingSide
    ? { backgroundColor: "var(--color-sr-salmon)", boxShadow: "0 0 0 1px var(--color-sr-salmon)" }
    : { backgroundColor: "var(--color-sr-mint)", boxShadow: "0 0 0 1px var(--color-sr-mint)" };
  const ctaClass = isSellingSide
    ? "bg-sr-salmon text-white hover:bg-sr-salmon/90"
    : "bg-sr-mint text-sr-espresso hover:brightness-105";
  const depositCtaClass = "bg-sr-lemon-muted text-sr-espresso hover:bg-sr-pink-light";
  const estimatedReceiveLabel = receiveToken?.label ?? "—";
  const averagePriceDisplay = orderType === "limit"
    ? (numericPrice > 0 ? limitPrice : "--")
    : (avgFillPrice || "--");
  const slippageDisplay = orderType === "market" ? `${SLIPPAGE_BUFFER_PCT}%` : "--";

  const subtotalValue = useMemo(() => {
    if (numericAmount <= 0) return null;
    if (!isSellingSide) return numericAmount;
    const receive = parseFloat(receiveAmount.replace(/,/g, ""));
    return Number.isFinite(receive) && receive > 0 ? receive : null;
  }, [isSellingSide, numericAmount, receiveAmount]);

  const feeAmount = subtotalValue !== null && feeRate !== null ? subtotalValue * feeRate : null;
  const totalValue = subtotalValue !== null && feeAmount !== null
    ? (isSellingSide ? Math.max(subtotalValue - feeAmount, 0) : subtotalValue + feeAmount)
    : null;

  const maxMarketDisplay = useMemo(() => {
    if (!spendToken || marketMaxRaw === null || marketMaxRaw <= 0n) return null;
    return formatAssetValue(spendToken.asset, marketMaxRaw);
  }, [marketMaxRaw, spendToken]);

  const formatSummaryNumber = useCallback((value: number | null, assetLabel?: string | null) => {
    if (value === null || !Number.isFinite(value)) return "--";
    const formatted = value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 6,
    });
    return assetLabel ? `${formatted} ${assetLabel}` : formatted;
  }, []);

  const summaryRows = [
    { label: "Slippage", value: slippageDisplay },
    {
      label: "Average Price",
      value: averagePriceDisplay !== "--" && quoteLabel && baseLabel
        ? `${averagePriceDisplay} ${baseLabel}-${quoteLabel}`
        : averagePriceDisplay,
    },
    { label: "Subtotal", value: formatSummaryNumber(subtotalValue, quoteLabel) },
    {
      label: "Trading fee",
      value: feePercent !== null
        ? `${formatSummaryNumber(feeAmount, quoteLabel)} (${feePercent.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 3,
        })}%)`
        : "--",
    },
    { label: "Total", value: formatSummaryNumber(totalValue, quoteLabel), emphasized: true },
  ];

  const needsDepositAction = !myAccount || !spendBalance || spendBalance.raw <= 0n;
  const ctaDisabled = needsDepositAction
    ? !selectedMarket || depositDisabled
    : (
      !selectedMarket ||
      numericAmount <= 0 ||
      isPlacingOrder ||
      (orderType === "market" && !hasLiquidity) ||
      (orderType === "limit" && !limitPrice.trim())
    );
  const ctaLabel = isPlacingOrder
    ? "Placing Order..."
    : needsDepositAction
      ? "Add funds to continue"
      : isSellingSide
        ? `Sell ${baseLabel ?? ""}`.trim()
        : `Buy ${baseLabel ?? ""}`.trim();

  const handleCtaClick = useCallback(() => {
    if (needsDepositAction) {
      onDeposit?.(spendToken ? marketAssetToWalletId(spendToken.asset) : undefined);
      return;
    }

    void handlePlaceOrder();
  }, [handlePlaceOrder, needsDepositAction, onDeposit, spendToken]);

  return {
    swapAmount,
    sliderValue,
    spendToken,
    quoteLabel,
    receiveToken,
    spendTokenOptions,
    spendBalance,
    hasLiquidity,
    effectiveMaxRaw,
    maxMarketDisplay,
    estimatedReceiveLabel,
    receiveAmount,
    summaryRows,
    accentTextClass,
    accentSurfaceClass,
    sliderFillClass,
    sliderDotClass,
    sliderThumbStyle,
    ctaClass,
    depositCtaClass,
    ctaDisabled,
    ctaLabel,
    handleSliderChange,
    setAmountFromPercent,
    handleAmountChange,
    handleSelectToken,
    handleCtaClick,
    setLimitPrice: onLimitPriceChange,
  };
}
